import { useRouter } from 'next/router';
import { ComponentProps } from 'react';
import styled from 'styled-components';
import { CFImage, WithId } from '../../lib/types';
import Footer from '../modules/Footer';
import PageBottomCTA from '../modules/PageBottomCTA';
import PageLinks from '../modules/PageLinks';
import BlogArchive, { BlogExcerpt } from './BlogArchive';
import FeaturePostHeader, { FeaturePostHeadingProps } from './FeaturePostHeader';
import Navbar from './Navbar';
import PageMeta from './PageMeta';
import PopularPosts, { PopularPost } from './PopularPosts';
import { searchParams } from '../../lib/utils';

export type FeaturedBlogPostType = WithId<{
  title: string
  slug: string
  featureImage: CFImage
  date: string
  featurePostExcerpt: string
}>;

export type BlogPageArchiveData = WithId<{
  title: string
  metaDescription: string
  bottomCtaModule: ComponentProps<typeof PageBottomCTA>,
  bottomPageLinks: ComponentProps<typeof PageLinks>,
  blogLogo?: CFImage,
  backgroundImage: CFImage
  featuredPost: FeaturedBlogPostType
  popularPosts: Array<PopularPost>,
  emailFormTitle: string
  emailFormDescription: string
  emailFormSubmitButtonText: string
}>;

type BlogPageProps = {
  postSummaries: Array<BlogExcerpt>
  currentPage: string
  blogLogo?: CFImage
  navbarData: ComponentProps<typeof Navbar>
  totalPosts: number
  blogPageArchive: BlogPageArchiveData
  blogExcerpts: Array<BlogExcerpt>
  categories: { [key: string]: number }
}

type BackgroundColorContainerProps = {
  useGreyBackground?: boolean;
}

const BackgroundColorContainer = styled.div<BackgroundColorContainerProps>`
  display: flex;
  background-color:  ${(props) => (props.useGreyBackground ? 'var(--color-charcoal-v02)' : 'var(--color-bluescape-v10)')};
  align-items:center;
  position: relative;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  z-index: 1;
  background-color: transparent;
  width: 100vw;
`;

const BlogPageContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  max-width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.medium}) {
    background-size: 150vw;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.small}) {
    background-size: 250vw;
  }
`;

const BlogPage = ({
  blogPageArchive, postSummaries, totalPosts, currentPage, navbarData, categories,
}: BlogPageProps) => {
  const { query } = useRouter();
  const { category, q } = query;
  const { blogLogo } = blogPageArchive;
  const shouldShowTop = !(category || q);
  const queryStr = searchParams({ page: parseInt(currentPage, 10) > 1 ? currentPage : undefined });

  const ogImgUrl = `${blogPageArchive.featuredPost.featureImage.url}?fm=png&w=1200&h=630&fit=fill`;
  const featurePostProps: FeaturePostHeadingProps = {
    title: blogPageArchive.featuredPost.title,
    blogLogo,
    backgroundImage: blogPageArchive.backgroundImage,
    image: blogPageArchive.featuredPost.featureImage,
    link: `/blog/${blogPageArchive.featuredPost.slug}`,
    navbarData: { ...navbarData, transparent: true, darkTheme: true },
    text: blogPageArchive.featuredPost.featurePostExcerpt,
    foregroundTheme: 'Bluescape Strong',
    tags: [{
      color: 'var(--color-bluescape-v40)', content: 'Featured post', large: true, textColor: 'var(--color-bluescape-dark)',
    }],
  };
  return (
    <div className="theme-bluescape-pale">
      {shouldShowTop && <FeaturePostHeader {...featurePostProps} />}
      <BackgroundColorContainer className="theme-charcoal-bleach">
        <PageMeta {...blogPageArchive} imageUrl={ogImgUrl} queryString={queryStr} slug="/blog" />
        <ContentContainer>
          <BlogPageContainer>
            {shouldShowTop && (
            <div className="theme-bluescape-pale">
              <PopularPosts popularPosts={blogPageArchive.popularPosts} />
            </div>
            )}
            <BlogArchive
              emailForSubmitButtonText={blogPageArchive.emailFormSubmitButtonText}
              emailFormDescription={blogPageArchive.emailFormDescription}
              emailFormTitle={blogPageArchive.emailFormTitle}
              categories={categories}
              blogExcerpts={postSummaries}
              totalPosts={totalPosts}
              currentPage={currentPage}
            />
          </BlogPageContainer>
          {blogPageArchive.bottomPageLinks && <PageLinks {...blogPageArchive.bottomPageLinks} />}
          {blogPageArchive.bottomCtaModule
            && <PageBottomCTA {...blogPageArchive.bottomCtaModule} />}
          <Footer {...navbarData} />
        </ContentContainer>
      </BackgroundColorContainer>
    </div>

  );
};
export default BlogPage;
